import React, { useState, useEffect } from 'react'
import {
  Modal,
  Table,
  InputNumber,
  Button,
  Space,
  Tag,
  Alert,
  Typography,
  Tooltip,
  Progress,
  Row,
  Col,
  Card,
  Statistic,
  message
} from 'antd'
import { InfoCircleOutlined, WarningOutlined, CheckCircleOutlined, DollarOutlined } from '@ant-design/icons'
import { formatCurrency } from '../../../../Billing-Module-Enhanced/utils/helpers'

const { Text, Title } = Typography

const EnhancedAllocationModal = ({
  visible,
  onCancel,
  invoice,
  totalAmountToAllocate,
  onAllocate,
  existingAllocations = []
}) => {
  const [allocations, setAllocations] = useState({})
  const [loading, setLoading] = useState(false)

  // Calculate total already allocated to this invoice
  const previouslyAllocated = existingAllocations
    .filter((alloc) => alloc.invoiceRef === invoice._id)
    .reduce((sum, alloc) => sum + parseFloat(alloc.paymentAmount || 0), 0)

  // Calculate what's still owed
  const invoiceTotal = invoice.billingRecords?.reduce((sum, item) => sum + parseFloat(item.total || 0), 0) || 0

  const medicalAidPortion =
    invoice.billingRecords?.reduce((sum, item) => {
      // Handle both field names: medicalAid and medicalAidDue
      const amount = parseFloat(item.medicalAid || item.medicalAidDue || 0)
      return sum + amount
    }, 0) || 0

  const patientPortion =
    invoice.billingRecords?.reduce((sum, item) => {
      // Handle both field names: patient and patientDue
      const amount = parseFloat(item.patient || item.patientDue || 0)
      return sum + amount
    }, 0) || 0

  const totalPaidToDate = previouslyAllocated
  const outstandingTotal = invoiceTotal - totalPaidToDate
  const outstandingMedicalAid = medicalAidPortion - totalPaidToDate

  // Calculate total currently being allocated
  const totalCurrentlyAllocating = Object.values(allocations).reduce((sum, alloc) => sum + (alloc.amount || 0), 0)

  const handleAllocationChange = (recordId, value) => {
    const maxAllowable = parseFloat(allocations[recordId]?.maxAmount || 0)

    setAllocations((prev) => ({
      ...prev,
      [recordId]: {
        ...prev[recordId],
        amount: Math.min(value || 0, maxAllowable)
      }
    }))
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      // Validate that we're not allocating to a private invoice
      if (invoice?.accountGroup === 'private' || invoice?.goodXStatus === 'Private' || !invoice?.goodXTicketNo) {
        message.error('Medical aid payments cannot be allocated to private invoices')
        setLoading(false)
        return
      }

      const allocationData = Object.entries(allocations)
        .filter(([_, alloc]) => alloc.amount > 0)
        .map(([recordId, alloc]) => ({
          billingRecordId: recordId,
          billingRecord: alloc.billingRecord,
          amount: alloc.amount
        }))

      await onAllocate(invoice, allocationData, totalCurrentlyAllocating)
      onCancel()
    } catch (error) {
      console.error('Allocation error:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Initialize allocations for each billing record
    const initialAllocations = {}
    invoice.billingRecords?.forEach((record) => {
      // Handle both field names: medicalAid and medicalAidDue
      const itemMedicalAid = parseFloat(record.medicalAid || record.medicalAidDue || 0)
      const alreadyPaid = existingAllocations
        .filter((alloc) => alloc.billingRecordId === record._id)
        .reduce((sum, alloc) => sum + parseFloat(alloc.paymentAmount || 0), 0)

      const itemOutstanding = itemMedicalAid - alreadyPaid

      initialAllocations[record._id] = {
        billingRecord: record,
        amount: 0,
        maxAmount: itemOutstanding,
        alreadyPaid
      }
    })
    setAllocations(initialAllocations)
  }, [invoice, existingAllocations])

  const columns = [
    {
      title: 'Service/Item',
      dataIndex: 'description',
      key: 'description',
      width: '30%'
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: '10%',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Medical Aid',
      dataIndex: 'medicalAid',
      key: 'medicalAid',
      width: '10%',
      align: 'right',
      render: (value, record) => {
        // Handle both field names: medicalAid and medicalAidDue
        const amount = parseFloat(record.medicalAid || record.medicalAidDue || 0)
        return formatCurrency(amount)
      }
    },
    {
      title: 'Already Paid',
      key: 'alreadyPaid',
      width: '10%',
      align: 'right',
      render: (_, record) => {
        const paid = allocations[record._id]?.alreadyPaid || 0
        return <Text type={paid > 0 ? 'success' : 'secondary'}>{formatCurrency(paid)}</Text>
      }
    },
    {
      title: 'Outstanding',
      key: 'outstanding',
      width: '10%',
      align: 'right',
      render: (_, record) => {
        const outstanding = allocations[record._id]?.maxAmount || 0
        return <Text type={outstanding > 0 ? 'danger' : 'success'}>{formatCurrency(outstanding)}</Text>
      }
    },
    {
      title: 'Allocate',
      key: 'allocate',
      width: '15%',
      align: 'center',
      render: (_, record) => {
        const allocation = allocations[record._id]
        const isFullyPaid = allocation?.maxAmount <= 0

        if (isFullyPaid) {
          return <Tag color='green'>Fully Paid</Tag>
        }

        return (
          <InputNumber
            value={allocation?.amount || 0}
            onChange={(value) => handleAllocationChange(record._id, value)}
            formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => value.replace(/R\s?|(,*)/g, '')}
            min={0}
            max={allocation?.maxAmount || 0}
            style={{ width: '100%' }}
          />
        )
      }
    },
    {
      title: 'Status',
      key: 'status',
      width: '15%',
      align: 'center',
      render: (_, record) => {
        const allocation = allocations[record._id]
        const willBeFullyPaid = allocation?.amount >= allocation?.maxAmount
        const partiallyPaid = allocation?.amount > 0 && allocation?.amount < allocation?.maxAmount
        const alreadyFullyPaid = allocation?.maxAmount <= 0

        if (alreadyFullyPaid) {
          return (
            <Tag color='green' icon={<CheckCircleOutlined />}>
              Paid
            </Tag>
          )
        }
        if (willBeFullyPaid) {
          return (
            <Tag color='blue' icon={<CheckCircleOutlined />}>
              Will be Paid
            </Tag>
          )
        }
        if (partiallyPaid) {
          return (
            <Tag color='orange' icon={<InfoCircleOutlined />}>
              Partial
            </Tag>
          )
        }
        return (
          <Tag color='red' icon={<WarningOutlined />}>
            Unpaid
          </Tag>
        )
      }
    }
  ]

  return (
    <Modal
      title={
        <Space direction='vertical' style={{ width: '100%' }}>
          <Title level={4}>Allocate Medical Aid Payment</Title>
          <Text type='secondary'>Invoice: {invoice.invoiceNo}</Text>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1200}
      footer={[
        <Button key='cancel' onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          loading={loading}
          onClick={handleSubmit}
          disabled={totalCurrentlyAllocating <= 0}
        >
          Confirm Allocation ({formatCurrency(totalCurrentlyAllocating)})
        </Button>
      ]}
    >
      <Space direction='vertical' style={{ width: '100%' }} size='large'>
        {/* Summary Cards */}
        <Row gutter={16}>
          <Col span={6}>
            <Card size='small'>
              <Statistic title='Invoice Total' value={invoiceTotal} prefix='R' precision={2} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size='small'>
              <Statistic
                title='Medical Aid Portion'
                value={medicalAidPortion}
                prefix='R'
                precision={2}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size='small'>
              <Statistic
                title='Already Paid'
                value={totalPaidToDate}
                prefix='R'
                precision={2}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card size='small'>
              <Statistic
                title='Outstanding'
                value={outstandingMedicalAid}
                prefix='R'
                precision={2}
                valueStyle={{ color: outstandingMedicalAid > 0 ? '#ff4d4f' : '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Billing Records Table */}
        <Table
          columns={columns}
          dataSource={invoice.billingRecords || []}
          pagination={false}
          size='small'
          rowKey='_id'
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell>
                <Text strong>Totals</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell align='right'>
                <Text strong>{formatCurrency(invoiceTotal)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell align='right'>
                <Text strong>{formatCurrency(medicalAidPortion)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell align='right'>
                <Text strong type='success'>
                  {formatCurrency(totalPaidToDate)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell align='right'>
                <Text strong type='danger'>
                  {formatCurrency(outstandingMedicalAid)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell align='center'>
                <Text strong type='primary'>
                  {formatCurrency(totalCurrentlyAllocating)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell />
            </Table.Summary.Row>
          )}
        />
      </Space>
    </Modal>
  )
}

export default EnhancedAllocationModal
